import { useQuery } from '@apollo/client/react';
import { DataTable } from '@optools/components/data-table/data-table';
import { DataTableAddButton } from '@optools/components/data-table/data-table-add-button';
import { DataTableColumnHeader } from '@optools/components/data-table/data-table-column-header';
import { ScrollPage } from '@optools/components/layout/layout';
import { PageLoader } from '@optools/components/loading/page-loader';
import { Button } from '@optools/components/ui/button';
import { GET_AUDIENCES } from '@optools/services/operator-gateway/esni/queries';
import { Audience } from '@optools/services/operator-gateway/esni/types';
import { CreateAudienceSheet } from '@optools/tools/esni/components/create-audience-sheet';
import { EsniDetailsSheet } from '@optools/tools/esni/components/esni-details-sheet';
import { useDetailsParams } from '@optools/tools/esni/hooks/useDetailsParams';
import { createFileRoute } from '@tanstack/react-router';
import { ColumnDef } from '@tanstack/react-table';
import { List } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';

export const Route = createFileRoute('/_auth/esni/audiences')({
  component: RouteComponent,
});

export function RouteComponent() {
  const [open, setOpen] = useState<boolean>(false);
  const { audienceHref, clearDetailsParam, setDetailsParam } = useDetailsParams();
  const { data, loading } = useQuery(GET_AUDIENCES);

  const [selectedAudience, setSelectedAudience] = useState<Audience | undefined>();
  const [createAudienceOpen, setCreateAudienceOpen] = useState(false);

  const onSheetClose = () => {
    clearDetailsParam();
    setOpen(false);
  };

  const onSelectAudience = useCallback(
    (audience: Audience) => {
      setSelectedAudience(audience);
      setDetailsParam('audience', audience.href);
      setOpen(true);
    },
    [setDetailsParam],
  );

  useEffect(() => {
    if (audienceHref.length) {
      const audience = data?.audiences.find(
        (audience) => audienceHref[audienceHref.length - 1] === audience.href,
      );

      if (audience) {
        setSelectedAudience(audience);
        setOpen(true);
      }
    }
  }, [data?.audiences, audienceHref]);

  const columns = useMemo<ColumnDef<Audience>[]>(() => {
    return [
      {
        accessorKey: 'href',
        header: ({ column }) => <DataTableColumnHeader column={column} title="Href" />,
        id: 'Href',
        sortingFn: (rowA, rowB) => {
          return rowA.original.href.localeCompare(rowB.original.href, undefined, {
            numeric: true,
            sensitivity: 'base',
          });
        },
      },
      {
        accessorKey: 'match',
        header: ({ column }) => <DataTableColumnHeader column={column} title="Match" />,
        id: 'Match',
      },
      {
        accessorKey: 'type',
        header: ({ column }) => <DataTableColumnHeader column={column} title="Type" />,
        id: 'Type',
      },
      {
        cell: ({ cell }) => {
          return (
            <div className="mr-4 flex justify-end">
              <Button
                aria-label="audience details"
                size="icon-sm"
                title="Audience Details"
                variant="outline"
                onClick={() => onSelectAudience(cell.row.original)}>
                <List />
              </Button>
            </div>
          );
        },
        id: 'details',
      },
    ];
  }, [onSelectAudience]);

  if (loading) return <PageLoader />;

  return (
    <ScrollPage>
      <DataTable columns={columns} data={data?.audiences ?? []}>
        <DataTableAddButton onClick={() => setCreateAudienceOpen(true)}>
          Add Audience
        </DataTableAddButton>
      </DataTable>
      <EsniDetailsSheet
        audienceHref={[selectedAudience?.href ?? '']}
        open={open}
        onOpenChange={onSheetClose}
      />

      <CreateAudienceSheet open={createAudienceOpen} onOpenChange={setCreateAudienceOpen} />
    </ScrollPage>
  );
}
